import { DataPlatformAudienceStatsRow } from '../data-platform.model';

const DAY_MS = 24 * 60 * 60 * 1000;

export function utcDay(date: Date): string {
  return date.toISOString().slice(0, 10);
}

export function addDays(day: string, days: number): string {
  return utcDay(new Date(Date.parse(`${day}T00:00:00Z`) + days * DAY_MS));
}

/** Rows whose members count is the size at the end of that day. */
export function nightlyRows(rows: readonly DataPlatformAudienceStatsRow[]): DataPlatformAudienceStatsRow[] {
  return rows.filter((row) => !row.backfilled);
}

export function rowsInRange<T extends { date: string }>(rows: readonly T[], from: string, to: string): T[] {
  return rows
    .filter((row) => row.date >= from && row.date <= to)
    .sort((one, other) => one.date.localeCompare(other.date));
}

export function groupByAudience(
  rows: readonly DataPlatformAudienceStatsRow[],
): Map<string, DataPlatformAudienceStatsRow[]> {
  const byAudience = new Map<string, DataPlatformAudienceStatsRow[]>();
  for (const row of rows) {
    const group = byAudience.get(row.audienceId);
    if (group) {
      group.push(row);
    } else {
      byAudience.set(row.audienceId, [row]);
    }
  }
  return byAudience;
}

/** The newest nightly row when it is for yesterday or today, and not older than `notBefore` (the day the rules last changed). */
export function currentSnapshotRow(
  rows: readonly DataPlatformAudienceStatsRow[],
  today: string,
  notBefore?: string,
): DataPlatformAudienceStatsRow | undefined {
  const latest = nightlyRows(rows).reduce<DataPlatformAudienceStatsRow | undefined>(
    (newest, row) => (!newest || row.date > newest.date ? row : newest),
    undefined,
  );
  const yesterday = addDays(today, -1);
  const earliest = notBefore && notBefore > yesterday ? notBefore : yesterday;
  return latest && latest.date >= earliest ? latest : undefined;
}

/** Every day from `from` to `to` has a row; today is not required, since it has no nightly row yet. */
export function coversRange(rows: readonly { date: string }[], from: string, to: string, today: string): boolean {
  const lastRequired = to < today ? to : addDays(today, -1);
  if (from > lastRequired) {
    return false;
  }
  const present = new Set(rows.map((row) => row.date));
  for (let day = from; day <= lastRequired; day = addDays(day, 1)) {
    if (!present.has(day)) {
      return false;
    }
  }
  return true;
}

export function definitionBreaks(rows: readonly Pick<DataPlatformAudienceStatsRow, 'definitionHash'>[]): number[] {
  return rows.flatMap((row, index) =>
    index > 0 && row.definitionHash !== rows[index - 1].definitionHash ? [index] : [],
  );
}
